import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { MatTooltip } from '@angular/material/tooltip';

import { DirectionService } from '../../direction.service';
import { UsersService } from 'src/app/super-management/user/users.service';
import { AuthService } from 'src/app/auth/auth.service';
import { DialogService } from 'src/app/dialog/dialog.service';

import { BranchesService } from 'src/app/super-management/branch/branches.service';
import { DataSharingService } from 'src/app/main-section/data-shering-service/data-sharing.service';
import {
  getPhPrintingNavIconSrc,
  getPhSystemTitleKeyFromProductId,
} from 'src/app/main-section/ph-printing-table/ph-service-data';
import { syncIsNotAcademyFromCluster, setIsNotAcademyLocalStorage } from 'src/app/utils/is-not-academy';

@Component({
  selector: 'app-right-place',
  templateUrl: './right-place.component.html',
  styleUrls: ['./right-place.component.css'],
  host: {
    class: 'fill-screen-modal-right-place'
  }
})
export class RightPlaceComponent implements OnInit, OnDestroy {
  isRTL: boolean = true;
  private directionSubscription: Subscription;
  isDarkMode: boolean = false;

  userId: string;
  userIsAuthenticated = false;
  private authStatusSub: Subscription;
  myPrintingServicesList: string[];
  myBranchesList: string[];
  branches: any[] = [];
  public printingService: string = '';
  public branch: string = '';
  isLoading: boolean = false;
  realBranchesObjects: any[] = [];
  realBranchesObjectsSelected: any[] = [];
  isIconReady: boolean = false;

  private fetchAttemptsCount: number = 0;
  private readonly maxFetchAttempts: number = 5;
  private intervalId: any = null;
  private isRequestCompleted: boolean = false;
  private isComponentDestroyed: boolean = false;

  // Loading tooltip
  showLoadingTooltip: boolean = false;
  private loadingTooltipTimeout: any = null;
  
  @ViewChild('loadingTooltip') loadingTooltipRef: MatTooltip;

  constructor(
    private directionService: DirectionService,
    private usersService: UsersService,
    private authService: AuthService,
    private dialogService: DialogService,
    private branchesService: BranchesService,
    private dataSharingService: DataSharingService,
    private router: Router,
  ) { }

  async ngOnInit() {
    this.isLoading = true;
    this.isIconReady = false;
    this.directionSubscription = this.directionService.direction$.subscribe(direction => {
      this.isRTL = direction === 'rtl';
    });

    this.directionService.isDarkMode$.subscribe(isDarkMode => {
      this.isDarkMode = isDarkMode;
    });
    
    // Subscribe to Material Icons ready state from global service
    this.directionService.materialIconsReady$.subscribe(ready => {
      this.isIconReady = ready;
    });

    this.userId = this.authService.getUserId();
    this.userIsAuthenticated = this.authService.getIsAuth();
    this.authStatusSub = this.authService
      .getAuthStatusListener()
      .subscribe(isAuthenticated => {
        this.userIsAuthenticated = isAuthenticated;
        this.userId = this.authService.getUserId();
      });
    this.usersService.getUser(this.userId).subscribe((user) => {
      this.myPrintingServicesList = [...(user.home_printingServices_list ?? [])];
      this.myBranchesList = [...(user.home_branches_list ?? [])];
    });
    await this.fetchBranchesWithRetry();
  }

  async fetchBranchesWithRetry() {
    this.fetchAttemptsCount = 0;
    this.isRequestCompleted = false;
    this.isComponentDestroyed = false;

    // Send first request immediately
    this.sendFetchRequest();

    // Set up interval to send requests every 4 seconds
    this.intervalId = setInterval(() => {
      if (this.isRequestCompleted || this.isComponentDestroyed) {
        this.stopFetching();
        return;
      }

      if (this.fetchAttemptsCount >= this.maxFetchAttempts) {
        // Max attempts reached, close dialog
        this.stopFetching();
        if (!this.isRequestCompleted && !this.isComponentDestroyed) {
          this.isLoading = false;
          this.closeRightPlaceDialog();
        }
        return;
      }

      // Send another request
      this.sendFetchRequest();
    }, 4000);
  }

  private async sendFetchRequest() {
    if (this.isRequestCompleted || this.isComponentDestroyed) {
      return;
    }

    this.fetchAttemptsCount++;

    try {
      await this.fetchBranches();
      
      // Check if request was already handled by another request
      if (this.isRequestCompleted || this.isComponentDestroyed) {
        return;
      }

      // Check if we got valid data
      if (this.realBranchesObjectsSelected?.length > 0) {
        this.handleSuccessfulResponse();
      } else {
        // No valid data, but request completed - will retry on next interval
        console.log('Request completed but no valid data, will retry...');
      }
    } catch (error) {
      // Request failed, will retry on next interval
      console.log('Request failed, will retry...', error);
      
      // Only close if we've exhausted all attempts
      if (this.fetchAttemptsCount >= this.maxFetchAttempts && !this.isRequestCompleted && !this.isComponentDestroyed) {
        this.stopFetching();
        this.isLoading = false;
        this.closeRightPlaceDialog();
      }
    }
  }

  private handleSuccessfulResponse() {
    if (this.isRequestCompleted || this.isComponentDestroyed) {
      return; // Already handled or component destroyed
    }

    // Mark as completed
    this.isRequestCompleted = true;

    // Stop the interval
    this.stopFetching();

    // Hide loading tooltip
    this.hideLoadingTooltip();

    // Update UI
    this.isLoading = false;
  }

  private stopFetching() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  async fetchBranches() {
    // Check if request was already completed
    if (this.isRequestCompleted) {
      return;
    }

    this.realBranchesObjects = await this.branchesService.getAllBranches().toPromise();

    // Check again after async operation
    if (this.isRequestCompleted) {
      return;
    }

    if (this.myBranchesList && this.realBranchesObjects && this.myBranchesList?.length > 0 && this.realBranchesObjects?.length > 0) {
      this.realBranchesObjectsSelected = [];

      for (let branchName of this.myBranchesList) {
        const selectedBranchIndex = this.realBranchesObjects.findIndex(
          (branch: any) => branch.name === branchName
        );
        if (selectedBranchIndex !== -1) {
          this.realBranchesObjectsSelected.push(this.realBranchesObjects[selectedBranchIndex]);
        } else {
          this.realBranchesObjectsSelected.push({ name: branchName });
        }
      }
      if (this.realBranchesObjectsSelected.length === 0) {
        throw new Error('Failed to process branches list');
      }
    } else {
      throw new Error('Missing required data for branches processing');
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  closeRightPlaceDialog() {
    this.dialogService.onCloseRightPlaceDialog();
  }

  onChooseServiceAndBranch(printingService: string, branch: string) {
    if (!printingService || !branch) {
      return;
    }
    this.printingService = printingService;
    this.branch = branch;
    this.onSetServiceAndBranch();
  }

  async onSetServiceAndBranch() {
    localStorage.setItem('printingService', this.printingService);
    localStorage.setItem('branch', this.branch);
    if (this.printingService === 'ph') {
      setIsNotAcademyLocalStorage(false);
    } else {
      const cluster = this.realBranchesObjects?.find(
        (b: any) => b.name === this.branch
      );
      syncIsNotAcademyFromCluster(cluster, this.printingService);
    }
    if (this.userIsAuthenticated) {
      this.isLoading = true;
      try {
        await this.dataSharingService.setPrintingService(this.printingService);
        await this.dataSharingService.setBranch(this.branch);
        if (this.printingService === 'ph') {
          await this.dataSharingService.setProduct(this.branch);
        }
        this.authService.updateAuthData(this.printingService, this.branch);
        this.usersService.updateUserPlace(
          this.printingService,
          this.branch
        ).subscribe(response => {
          this.closeRightPlaceDialog();
          if (this.printingService === 'ph') {
            this.router.navigate(['/phprint']);
          } else {
            this.router.navigate(['/print']);
          }
        });
        this.isLoading = false;
      } catch (error) {
        this.isLoading = false;
        this.closeRightPlaceDialog();
        console.error('Error changing printing service and branch:', error);
      }
    }
  }

  ngOnDestroy() {
    // Mark component as destroyed to stop all processes
    this.isComponentDestroyed = true;
    
    // Stop interval if exists
    this.stopFetching();
    
    // Hide loading tooltip
    this.hideLoadingTooltip();
    
    // Mark as completed to prevent any pending requests from updating state
    this.isRequestCompleted = true;

    if (this.directionSubscription) {
      this.directionSubscription.unsubscribe();
    }
    if (this.authStatusSub) {
      this.authStatusSub.unsubscribe();
    }
  }

  goHome() {
    this.router.navigate(['/']);
    this.closeRightPlaceDialog();
  }

  getPrintingService(index: number) {
    if (this.myPrintingServicesList?.length > 0 &&
      (this.myPrintingServicesList[index] === 'plotter' ||
        this.myPrintingServicesList[index] === 'ph')) {
      return this.myPrintingServicesList[index];
    } else {
      if (this.realBranchesObjectsSelected?.length > 0 && this.realBranchesObjectsSelected[index]?.express?.isPicsPrinter) {
        return "pics";
      } else {
        return "express";
      }
    }
  }

  /**
   * For PH, home_branches_list holds the product id; printingService is always `ph`.
   * אייקון: assets/images/phprint-icon.svg או 3dprint-icon.svg (תואם choose-product / ניווט PH).
   */
  readonly getPhPrintingServiceIconSrc = getPhPrintingNavIconSrc;

  /** קידומת טקסט: «הדפסה במשלוח» מול «הדפסת תלת מימד». */
  readonly getPhSystemTitleKeyFromProductId = getPhSystemTitleKeyFromProductId;

  // Show loading tooltip when clicking on dialog while loading
  onDialogClick() {
    if (this.isLoading && !this.showLoadingTooltip) {
      this.showLoadingTooltip = true;
      
      // Show tooltip programmatically after Angular updates the binding
      setTimeout(() => {
        if (this.loadingTooltipRef && this.showLoadingTooltip) {
          this.loadingTooltipRef.show();
        }
      }, 50);
      
      // Hide tooltip after 3 seconds
      this.loadingTooltipTimeout = setTimeout(() => {
        this.hideLoadingTooltip();
      }, 3000);
    }
  }

  private hideLoadingTooltip() {
    this.showLoadingTooltip = false;
    
    // Hide tooltip programmatically
    if (this.loadingTooltipRef) {
      this.loadingTooltipRef.hide();
    }
    
    if (this.loadingTooltipTimeout) {
      clearTimeout(this.loadingTooltipTimeout);
      this.loadingTooltipTimeout = null;
    }
  }

  // ===============
}
